/* Optional 24-hour controller for the theme's existing countdown markup.
 * Uses the native countdown-timer-flip elements and their existing animation.
 * Fixed-date countdowns never load or use this controller.
 * A shared timestamp anchors every cycle; visiting or refreshing does not reset it.
 * This is a display clock, not a discount scheduler or an order/cart mutation.
 */
const DAY_MS = 24 * 60 * 60 * 1000;

// An expired anchor is intentional: advance by whole 24-hour cycles without
// iteration. Invalid/blank dates use shared UTC midnight, never first-visit time.
export function dailyCycle(now, anchor = 0) {
  if (!Number.isFinite(now)) throw new TypeError('A finite current timestamp is required.');
  if (!Number.isFinite(anchor)) anchor = 0;
  const elapsed = ((now - anchor) % DAY_MS + DAY_MS) % DAY_MS;
  const remaining = DAY_MS - elapsed;
  return { remaining, nextReset: now + remaining };
}

if (!customElements.get('debacq-daily-countdown')) {
  class DebacqDailyCountdown extends HTMLElement {
    connectedCallback() {
      // Reconnects and theme-editor reloads must not accumulate clocks/listeners.
      this.disconnectedCallback();
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      const parsed = Date.parse(this.getAttribute('expires-at') || '');
      this.anchor = Number.isFinite(parsed) ? parsed : 0;
      this.label = this.getAttribute('aria-label') || 'Time until the next daily reset';
      this.lastReset = null;
      this.lastSeconds = null;
      this.flips = null;
      this.visible = true;
      this.ready = false;
      const sync = () => this.sync();
      document.addEventListener('visibilitychange', sync, { signal });
      window.addEventListener('pageshow', sync, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (signal.aborted) return;
          this.visible = entries[entries.length - 1].isIntersecting;
          this.sync();
        }, { rootMargin: '500px' });
        this.observer.observe(this);
      }
      // A module can be fetched before the theme has registered its native flips.
      // Keep the controller dormant until their public updateValue method exists.
      customElements.whenDefined('countdown-timer-flip').then(() => {
        if (signal.aborted || !this.isConnected) return;
        customElements.upgrade(this);
        this.flips = Object.fromEntries([...this.querySelectorAll('countdown-timer-flip')]
          .map(flip => [flip.getAttribute('type'), flip]));
        this.ready = true;
        this.sync();
      });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.observer?.disconnect();
      this.observer = null;
      clearTimeout(this.tick);
      this.tick = null;
      this.ready = false;
      // Keep the original label rather than appending dates on each reconnect.
      if (this.label) this.setAttribute('aria-label', this.label);
    }

    sync() {
      clearTimeout(this.tick);
      this.tick = null;
      if (!this.ready || !this.isConnected || this.lifecycle.signal.aborted || document.hidden || !this.visible) return;
      this.update();
      // Align to the next *cycle* second, recomputing from wall-clock time on
      // every tick. Sleeping tabs and device suspension cannot accumulate drift.
      const now = Date.now();
      const phase = ((now - this.anchor) % 1000 + 1000) % 1000;
      this.tick = setTimeout(() => this.sync(), 1000 - phase + 15);
    }

    update() {
      const { remaining, nextReset } = dailyCycle(Date.now(), this.anchor);
      const seconds = Math.ceil(remaining / 1000);
      if (seconds !== this.lastSeconds) {
        this.lastSeconds = seconds;
        const values = {
          days: 0,
          hours: Math.floor(seconds / 3600),
          minutes: Math.floor(seconds / 60) % 60,
          seconds: seconds % 60
        };
        for (const [type, value] of Object.entries(values)) {
          this.flips[type]?.updateValue(value);
        }
      }
      if (nextReset !== this.lastReset) {
        this.lastReset = nextReset;
        // A descriptive, non-live label, not a permanently expired date.
        let date;
        try {
          date = new Intl.DateTimeFormat(document.documentElement.lang || undefined,
            { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(nextReset));
        } catch {
          date = new Date(nextReset).toISOString();
        }
        this.setAttribute('aria-label', `${this.label}: ${date}`);
      }
    }
  }
  customElements.define('debacq-daily-countdown', DebacqDailyCountdown);
}
